import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { SpaceProps } from 'styled-system';
import { space } from 'styled-system';

import { Flex } from 'common/components/Flex';
import ArticleSmallPrice, { RenderingStrategy } from 'common/components/Price/ArticleSmallPrice';
import { HeadlineH8, SubtitleST4 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import { textWithEllipsis } from 'common/theme/styles';
import type { ArticleDeliveryFlags, ArticlePrices } from 'common/types/Article';
import type { ArticleLabelsList } from 'common/types/labels';
import type { RecommendationItemArticle } from 'common/types/recommendation';

interface RecommendationSlideDetailsProps {
  name: string;
  prices: ArticlePrices;
  delivery: Nullable<{
    flags: ArticleDeliveryFlags;
  }>;
  energyClass?: RecommendationItemArticle['energyClass'];
  labels_v2?: ArticleLabelsList;
}

const prominentPricingOptions = {
  priceComponent: HeadlineH8,
  withMarketingRed: true,
  withLargerEnergyClass: true,
  withLargerOldPrice: true,
};

const RecommendationSlideDetailsContainer = (props: ComponentProps<typeof Flex>) => (
  <Flex flexDirection="column" p={['sp_4', null, null, 'sp_8']} {...props} />
);

const UnstyledArticleSmallPrice = styled(ArticleSmallPrice)<SpaceProps>`
  ${space};
`;

const StyledArticleSmallPrice = (props: ComponentProps<typeof UnstyledArticleSmallPrice>) => (
  <UnstyledArticleSmallPrice mb={['sp_8', null, null, 'sp_12']} {...props} />
);

const UnstyledArticleName = styled(SubtitleST4)`
  ${textWithEllipsis(2)};
`;

const ArticleName = (props: ComponentProps<typeof UnstyledArticleName>) => (
  <UnstyledArticleName as="p" m="sp_0" mb={['sp_8', null, null, 'sp_12']} {...props} />
);

const RecommendationSlideDetails = ({
  name,
  prices: { regular, special, rrp, measurement },
  delivery,
  energyClass,
  labels_v2,
}: RecommendationSlideDetailsProps) => {
  const { t } = useLocalization();

  return (
    <RecommendationSlideDetailsContainer>
      <StyledArticleSmallPrice
        energyClass={energyClass}
        specialPrice={special}
        rrpPrice={rrp}
        regularPrice={regular}
        measurement={measurement}
        renderingStrategy={RenderingStrategy.Horizontal}
        {...prominentPricingOptions}
        labels_v2={labels_v2}
      />
      <ArticleName data-testid="recommendation-slide-name">{name}</ArticleName>
      {delivery?.flags?.fastDelivery && (
        <SubtitleST4 color="sys.success.text.default" data-testid="fast-delivery">
          {t('h24_immediately_available')}
        </SubtitleST4>
      )}
    </RecommendationSlideDetailsContainer>
  );
};

export default RecommendationSlideDetails;
